import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-hot-toast';
import * as recruitmentService from '../services/recruitmentService';
import { RecruitmentJob, Candidate, Application } from '../services/recruitmentService';
import JobForm from '../components/recruitment/JobForm';
import PipelineBoard from '../components/recruitment/PipelineBoard';
import EmbedCodeGenerator from '../components/recruitment/EmbedCodeGenerator';

const Recruitment: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get active tab from URL hash
  const getInitialTab = () => {
    const hash = location.hash.replace('#', '');
    const validTabs = ['dashboard', 'jobs', 'pipeline', 'applications', 'interviews', 'offers', 'candidates', 'analytics', 'embed'];
    return validTabs.includes(hash) ? hash : 'dashboard';
  };

  const [activeTab, setActiveTab] = useState<string>(getInitialTab());
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [jobs, setJobs] = useState<RecruitmentJob[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  
  // Modal states
  const [showJobForm, setShowJobForm] = useState(false);
  const [selectedJob, setSelectedJob] = useState<RecruitmentJob | null>(null);

  // Update active tab when hash changes
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  // Change URL hash when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`#${tab}`, { replace: true });
  };

  // Load initial data
  useEffect(() => {
    if (!user) return;
    loadData();
  }, [user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [jobsData, applicationsData, candidatesData, analyticsData] = await Promise.all([
        recruitmentService.getJobs(),
        recruitmentService.getApplications(),
        recruitmentService.getCandidates(),
        recruitmentService.getRecruitmentAnalytics()
      ]);
      
      setJobs(jobsData);
      setApplications(applicationsData);
      setCandidates(candidatesData);
      setAnalytics(analyticsData);
    } catch (error: any) {
      console.error('Failed to load recruitment data:', error);
      toast.error('Failed to load recruitment data');
    } finally {
      setLoading(false);
    }
  };

  // Redirect if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'jobs', label: 'Jobs', icon: '💼' },
    { id: 'pipeline', label: 'Pipeline', icon: '📋' },
    { id: 'applications', label: 'Applications', icon: '📄' },
    { id: 'embed', label: 'Embed Portal', icon: '🌐' },
    { id: 'interviews', label: 'Interviews', icon: '📅' },
    { id: 'offers', label: 'Offers', icon: '✉️' },
    { id: 'candidates', label: 'Talent Pool', icon: '👥' },
    { id: 'analytics', label: 'Analytics', icon: '📈' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl flex items-center justify-center text-white text-xl">
                👔
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Recruitment Portal</h1>
                <p className="text-xs text-gray-500">Applicant Tracking System</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link
                to="/help?article=recruitment-tabs"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50"
              >
                ❓ Help
              </Link>
              <button
                onClick={() => {
                  setSelectedJob(null);
                  setShowJobForm(true);
                }}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium text-sm flex items-center gap-2"
              >
                <span>+</span>
                <span>Post Job</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-px -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'border-purple-600 text-purple-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.id === 'jobs' && jobs.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                    {jobs.filter(j => j.status === 'active').length}
                  </span>
                )}
                {tab.id === 'applications' && applications.length > 0 && (
                  <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                    {applications.filter(a => a.status === 'active').length}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && (
              <DashboardPlaceholder analytics={analytics} jobs={jobs} applications={applications} />
            )}
            {activeTab === 'jobs' && (
              <JobsPlaceholder 
                jobs={jobs} 
                onRefresh={loadData}
                onEdit={(job) => {
                  setSelectedJob(job);
                  setShowJobForm(true);
                }}
              />
            )}
            {activeTab === 'pipeline' && (
              <PipelineBoard applications={applications} jobs={jobs} onRefresh={loadData} />
            )}
            {activeTab === 'applications' && (
              <ApplicationsPlaceholder applications={applications} onRefresh={loadData} />
            )}
            {activeTab === 'interviews' && (
              <InterviewsPlaceholder />
            )}
            {activeTab === 'offers' && (
              <OffersPlaceholder />
            )}
            {activeTab === 'candidates' && (
              <CandidatesPlaceholder candidates={candidates} onRefresh={loadData} />
            )}
            {activeTab === 'analytics' && (
              <AnalyticsPlaceholder analytics={analytics} />
            )}
            {activeTab === 'embed' && (
              <EmbedCodeGenerator 
                companyId={user?.id || ''} 
                companyName={user?.email?.split('@')[0] || 'Your Company'} 
              />
            )}
          </>
        )}
      </div>

      {/* Job Form Modal */}
      {showJobForm && (
        <JobForm
          job={selectedJob}
          onClose={() => {
            setShowJobForm(false);
            setSelectedJob(null);
          }}
          onSuccess={() => {
            loadData();
          }}
        />
      )}
    </div>
  );
};

// Placeholder components (will replace with real components)
const DashboardPlaceholder: React.FC<{ analytics: any; jobs: RecruitmentJob[]; applications: Application[] }> = ({ analytics, jobs, applications }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Recruitment Dashboard</h2>
    
    {/* Key Metrics */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Active Jobs</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{analytics?.activeJobs || 0}</p>
          </div>
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl">💼</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          {analytics?.totalJobs || 0} total jobs
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Total Applications</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{analytics?.totalApplications || 0}</p>
          </div>
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl">📄</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          {applications.filter(a => a.current_stage === 'applied').length} new this week
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Interviews Scheduled</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">
              {applications.filter(a => a.current_stage === 'interview').length}
            </p>
          </div>
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl">📅</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          {applications.filter(a => a.current_stage === 'phone_screen').length} phone screens pending
        </p>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">Offers Extended</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">{analytics?.totalOffers || 0}</p>
          </div>
          <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
            <span className="text-2xl">✉️</span>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-4">
          {analytics?.acceptedOffers || 0} accepted
        </p>
      </div>
    </div>

    {/* Recent Activity */}
    <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Applications</h3>
      <div className="space-y-3">
        {applications.slice(0, 5).map((app: any) => (
          <div key={app.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                <span className="text-lg">👤</span>
              </div>
              <div>
                <p className="font-medium text-gray-900">
                  {app.candidate?.first_name} {app.candidate?.last_name}
                </p>
                <p className="text-sm text-gray-500">{app.job?.title}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                app.current_stage === 'offer' ? 'bg-green-100 text-green-700' :
                app.current_stage === 'interview' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {app.current_stage}
              </span>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(app.applied_at).toLocaleDateString()}
              </p>
            </div>
          </div>
        ))}
        {applications.length === 0 && (
          <p className="text-center text-gray-500 py-8">No applications yet</p>
        )}
      </div>
    </div>
  </div>
);

const JobsPlaceholder: React.FC<{ jobs: RecruitmentJob[]; onRefresh: () => void; onEdit: (job: RecruitmentJob) => void }> = ({ jobs, onRefresh, onEdit }) => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <h2 className="text-2xl font-bold text-gray-900">Jobs</h2>
      <button
        onClick={onRefresh}
        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
      >
        Refresh
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {jobs.map((job) => (
        <div 
          key={job.id} 
          onClick={() => onEdit(job)}
          className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
        >
          <div className="flex items-start justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
              job.status === 'active' ? 'bg-green-100 text-green-700' :
              job.status === 'draft' ? 'bg-gray-100 text-gray-700' :
              'bg-red-100 text-red-700'
            }`}>
              {job.status}
            </span>
          </div>
          <div className="space-y-2 text-sm text-gray-600">
            {job.department && <p>📁 {job.department}</p>}
            {job.location && <p>📍 {job.location}</p>}
            {job.employment_type && <p>⏰ {job.employment_type}</p>}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs text-gray-500">
              Posted {job.posted_at ? new Date(job.posted_at).toLocaleDateString() : 'Not published'}
            </p>
          </div>
        </div>
      ))}
      {jobs.length === 0 && (
        <div className="col-span-full text-center py-12 bg-white rounded-xl border border-gray-200">
          <span className="text-4xl mb-4 block">💼</span>
          <p className="text-gray-500">No jobs yet. Create your first job posting!</p>
        </div>
      )}
    </div>
  </div>
);

const PipelinePlaceholder: React.FC<{ applications: Application[]; jobs: RecruitmentJob[]; onRefresh: () => void }> = ({ applications }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Pipeline</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">📋</span>
      <p className="text-gray-500">Kanban pipeline board coming soon!</p>
      <p className="text-sm text-gray-400 mt-2">{applications.length} total applications</p>
    </div>
  </div>
);

const ApplicationsPlaceholder: React.FC<{ applications: Application[]; onRefresh: () => void }> = ({ applications }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Applications ({applications.length})</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">📄</span>
      <p className="text-gray-500">Applications table coming soon!</p>
    </div>
  </div>
);

const InterviewsPlaceholder: React.FC = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Interviews</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">📅</span>
      <p className="text-gray-500">Interview calendar coming soon!</p>
    </div>
  </div>
);

const OffersPlaceholder: React.FC = () => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Offers</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">✉️</span>
      <p className="text-gray-500">Offers manager coming soon!</p>
    </div>
  </div>
);

const CandidatesPlaceholder: React.FC<{ candidates: Candidate[]; onRefresh: () => void }> = ({ candidates }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Talent Pool ({candidates.length})</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">👥</span>
      <p className="text-gray-500">Talent pool coming soon!</p>
    </div>
  </div>
);

const AnalyticsPlaceholder: React.FC<{ analytics: any }> = ({ analytics }) => (
  <div className="space-y-6">
    <h2 className="text-2xl font-bold text-gray-900">Analytics</h2>
    <div className="bg-white rounded-xl shadow-sm p-8 border border-gray-200 text-center">
      <span className="text-4xl mb-4 block">📈</span>
      <p className="text-gray-500">Recruitment analytics coming soon!</p>
      {analytics && (
        <div className="mt-4 text-sm text-gray-600">
          <p>Active Jobs: {analytics.activeJobs}</p>
          <p>Total Applications: {analytics.totalApplications}</p>
        </div>
      )}
    </div>
  </div>
);

export default Recruitment;
